import { CommonModule } from "@angular/common";
import { Component, Input, output } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import { IInvoice } from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { MenuModule } from "primeng/menu";
import { RippleModule } from "primeng/ripple";
import { TableModule, TableRowCollapseEvent, TableRowExpandEvent } from "primeng/table";
import { CommonComponent } from "../../../../../../lib/components/common/common.component.mjs";
import { InvoiceService } from "../../../../../shared/services/financial/invoice.service.mjs";

@Component({
  selector: "app-invoices-card",
  standalone: true,
  imports: [
    CommonModule,
    BlockUIModule,
    CardModule,
    TableModule,
    ButtonModule,
    RippleModule,
    ConfirmDialogModule,
    RouterLink,
    MenuModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: "./invoice-card.component.html",
  styleUrls: ["./invoice-card.component.scss"],
})
export class InvoiceCardComponents extends CommonComponent {
  @Input()
  invoices: IInvoice[] = [];

  forceRefresh = output<boolean>();

  expanded: boolean = false;
  isSubLoading = false;

  expandedRows = {};

  expandAll() {
    this.expandedRows = this.invoices.reduce(
      (acc: any, p) => (acc[p.id] = true) && acc,
      {}
    );
  }

  collapseAll() {
    this.expandedRows = {};
  }

  onRowExpand(event: TableRowExpandEvent) {
    this.messageService.add({
      severity: "info",
      summary: "Product Expanded",
      detail: event.data.name,
      life: 3000,
    });
  }

  onRowCollapse(event: TableRowCollapseEvent) {
    this.messageService.add({
      severity: "success",
      summary: "Product Collapsed",
      detail: event.data.name,
      life: 3000,
    });
  }

  items: MenuItem[] = [
    {
      label: "View",
      icon: "pi pi-file",
      command: () => this.view(this.selectedInvoice!.id),
    },
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => this.edit(this.selectedInvoice!.id),
    },
    {
      label: "Download",
      icon: "pi pi-download",
      command: () => this.download(this.selectedInvoice!.document_url),
    },
    {
      label: "Mark Paid",
      icon: "pi pi-dollar",
      command: () => this.markPaid(this.selectedInvoice!.id),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => this.delete(this.selectedInvoice!.id),
    },
  ];

  async markPaid(invoice_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to mark this invoice as paid?",
      header: "Mark Invoice Paid",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Mark Invoice Paid",
        severity: "primary",
      },
      accept: async () => {
        this.isSubLoading = true;

        this.invoiceService
          .markPaid(invoice_id)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice marked as paid ....",
              });
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to mark invoice paid ....",
              });
            }
          })
          .finally(() => {
            this.isSubLoading = false;
          });
      },
    });
  }

  edit(invoice_id: string) {
    this.router.navigate(["/admin/invoices/edit/", invoice_id]);
  }

  view(invoice_id: string) {
    this.router.navigate(["/admin/invoices/view/", invoice_id]);
  }

  download(document_url: string | undefined) {
    if (document_url) {
      window.open(document_url, "_blank");
    }
  }

  async delete(id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to delete this invoice?",
      header: "Delete Invoice",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Delete Invoice",
      },
      accept: async () => {
        this.isSubLoading = true;

        const invoiceResult = await this.invoiceService.get(id);

        if (invoiceResult.status !== FUNCTION_RESULT_STATUS.SUCCESS)
        {
          return;
        }

        await this.invoiceService
          .delete(invoiceResult.data!)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice deleted successfully ..... redirecting",
              });
              this.router.navigate([
                "/admin/customers/view",
                invoiceResult.data?.customer_id.id,
              ]);
              this.forceRefresh.emit(true);
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Failed",
                detail: "Failed to delete the invoice",
              });
            }
          })
          .finally(() => {
            this.isSubLoading = false;
          });
      },
    });
  }

  constructor(
    protected router: Router,
    protected invoiceService: InvoiceService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    super();
  }

  selectedInvoice: IInvoice | undefined;

  rowSelected() {
    console.log("Customer", this.selectedInvoice);
    this.router.navigate(["/admin/customers/view/", this.selectedInvoice?.id]);
  }
}
